/* eslint-disable no-unused-vars */
// Effets d'initialisation / prefill / restauration du simulateur BATICO,
// extraits de useCalculateurBatico (ctx-bag). Aucun handler de calcul ici.
import { useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { getToken } from '../../services/tokenService';
import { API } from './constants';
import { useIaActivitesPrefill } from './useIaActivitesPrefill';
import { useProlongationPrefill } from './useProlongationPrefill';
import { useRelancePrefill } from './useRelancePrefill';
import { ecrireProrataConfig } from './prorataAvenant';

export function useBaticoEffects(deps) {
  const {
    contratParent, eligibilityForm, location, membreData, modeCalcul, remiseAutoAppliedRef,
    restrictions, setFormData, setIsFtpeMember, setLoadingRestrictions, setMembreData, setModeCalcul,
    setModeSimulateur, setMontantsPersonnalises, setOperationNominative, setParametresBatico, setPropositionConversion, setRemiseSolidaireNegociee,
    setRestrictions, setResumeMode, setResumeParentDevisId, setResumeSimulationId, setSelectedActivites2nd, setSelectedActivitesGO,
    setSelectedActivitesPIB, setSelectedActivitesTP, setTaxesPersonnalisees, setTerritoiresIntervention, setTypesOperation, setWalletBalance,
    setRepriseSimulateur, setValiditePersonnalisee, setContratParent,
    autoCalcDoneRef, setResultat,
    user,
  } = deps;

  useEffect(() => {
    const loadOptions = async () => {
      try {
        const response = await axios.get(`${API}/batico/config/v3/options-calculateur`);
        if (response.data.success) {
          if (response.data.types_operation?.length > 0) {
            setTypesOperation(response.data.types_operation);
          }
          if (response.data.territoires_intervention?.length > 0) {
            setTerritoiresIntervention(response.data.territoires_intervention);
          }
        }
      } catch (error) {
      }
    };
    loadOptions();
  }, []);

  useEffect(() => {
    const loadWallet = async () => {
      const token = getToken();
      if (!token || !user) return;
      
      try {
        const response = await axios.get(`${API}/wallets/me`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setWalletBalance(response.data.credit_balance);
      } catch (error) {
        setWalletBalance(null);
      }
    };
    loadWallet();
  }, [user]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const prefill = params.get('prefill');
    const resume = params.get('resume');
    const nouveau = params.get('nouveau');
    
    // MODE REPRISE: Reprendre une simulation existante
    if (resume === 'true') {
      setResumeMode(true);
      setResumeSimulationId(params.get('simulation_id'));
      
      const membreInfo = {
        membre_id: params.get('membre_id') || '',
        raison_sociale: params.get('raison_sociale') || '',
        siret: params.get('siret') || '',
        nom: params.get('nom') || '',
        prenom: params.get('prenom') || '',
        email: params.get('email') || '',
        telephone: params.get('telephone') || '',
        adresse: params.get('adresse') || '',
        code_postal: params.get('code_postal') || '',
        ville: params.get('ville') || '',
        statut_juridique: params.get('statut_juridique') || params.get('forme_juridique') || '',
        territoire: params.get('territoire') || '',
      };
      setMembreData(membreInfo);
      
      // Pré-remplir le territoire
      const territoire = params.get('territoire');
      if (territoire) {
        // Normaliser le territoire pour correspondre aux options du sélecteur
        const territoireNormalise = territoire.toLowerCase();
        // Le sélecteur de territoire sera mis à jour via setSelectedTerritory
      }
      
      // Pré-remplir le CA et nombre de jours
      const caHt = params.get('ca_ht');
      const nbJours = params.get('nombre_jours');
      if (caHt || nbJours) {
        setFormData(prev => ({
          ...prev,
          ca_ht: caHt || prev.ca_ht,
          nombre_jours: nbJours || prev.nombre_jours
        }));
      }
      
      // Pré-remplir les activités (JSON encodé)
      const activitesJson = params.get('activites');
      if (activitesJson) {
        try {
          const activites = JSON.parse(activitesJson);
          if (Array.isArray(activites) && activites.length > 0) {
            // Les activités seront sélectionnées via le composant SelecteurActivites
            // On stocke temporairement pour les sélectionner après le chargement
            setMembreData(prev => ({ ...prev, activites_preselect: activites }));
          }
        } catch (e) {
        }
      }
      
      // Pré-remplir type d'adhésion
      const typeAdhesion = params.get('type_adhesion');
      if (typeAdhesion === 'nominative') {
        // Activer le mode nominatif
        setOperationNominative(prev => ({
          ...prev,
          type_operation: params.get('type_operation') || '',
          adresse_operation: params.get('adresse_operation') || '',
          code_postal_operation: params.get('code_postal_operation') || '',
          ville_operation: params.get('ville_operation') || '',
          description_operation: params.get('description_operation') || ''
        }));
      }
      
      toast.success('Simulation BATICO chargée', {
        description: `Reprise de la simulation ${params.get('simulation_id')?.slice(0, 8)}... pour ${membreInfo.raison_sociale || 'ce membre'}`
      });
    }
    // MODE NOUVEAU: Nouvelle simulation avec données membre pré-remplies
    else if (nouveau === 'true') {
      const membreInfo = {
        membre_id: params.get('membre_id') || '',
        raison_sociale: params.get('raison_sociale') || '',
        siret: params.get('siret') || '',
        nom: params.get('nom') || '',
        prenom: params.get('prenom') || '',
        email: params.get('email') || '',
        telephone: params.get('telephone') || '',
        adresse: params.get('adresse') || '',
        code_postal: params.get('code_postal') || '',
        ville: params.get('ville') || '',
        statut_juridique: params.get('statut_juridique') || params.get('forme_juridique') || '',
        territoire: params.get('territoire') || '',
      };
      setMembreData(membreInfo);
      
      toast.info('Nouvelle simulation BATICO', {
        description: `Pour ${membreInfo.raison_sociale || 'ce membre'}`
      });
    }
    // MODE PREFILL classique (legacy)
    else if (prefill === 'true') {
      const membreInfo = {
        membre_id: params.get('membre_id') || '',
        raison_sociale: params.get('raison_sociale') || '',
        siret: params.get('siret') || '',
        nom: params.get('nom') || '',
        prenom: params.get('prenom') || '',
        email: params.get('email') || '',
        telephone: params.get('telephone') || '',
        adresse: params.get('adresse') || '',
        code_postal: params.get('code_postal') || '',
        ville: params.get('ville') || '',
        statut_juridique: params.get('statut_juridique') || params.get('forme_juridique') || '',
        territoire: params.get('territoire') || 'Guadeloupe',
        activite: params.get('activite') || ''
      };
      
      setMembreData(membreInfo);
      
      // Pré-sélectionner le territoire si fourni
      if (membreInfo.territoire) {
        setOperationNominative(prev => (prev.territoire_intervention ? prev : { ...prev, territoire_intervention: membreInfo.territoire }));
      }
      
      toast.success(`Données de ${membreInfo.prenom} ${membreInfo.nom} pré-remplies`, {
        description: 'Les informations du membre ont été chargées automatiquement'
      });
    }
  }, [location.search]);

  // MODE IA ACTIVITÉS — Phase 2 (extrait dans useIaActivitesPrefill)
  useIaActivitesPrefill({
    location, autoCalcDoneRef, setFormData,
    setSelectedActivitesGO, setSelectedActivites2nd, setSelectedActivitesTP, setSelectedActivitesPIB,
  });
  useProlongationPrefill({
    location, autoCalcDoneRef, setFormData, setModeCalcul, setMembreData, setOperationNominative,
    setSelectedActivitesGO, setSelectedActivites2nd, setSelectedActivitesTP, setSelectedActivitesPIB,
  });
  useRelancePrefill({
    location, autoCalcDoneRef, setFormData, setModeCalcul, setMembreData,
    setSelectedActivitesGO, setSelectedActivites2nd, setSelectedActivitesTP, setSelectedActivitesPIB,
  });


  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const targetMembreId = params.get('membre_id') || params.get('membre');
    if (!targetMembreId) return;
    if (!user || !['super_admin', 'admin', 'cooper'].includes(user.role)) return;

    // Si membreData a déjà des champs RH ⇒ rien à faire (déjà enrichi
    // par un snapshot ou par une exécution précédente de ce useEffect).
    const alreadyEnriched = !!(
      membreData
      && (
        membreData.effectif
        || membreData.date_creation_entreprise
        || membreData.experience_professionnelle_annees != null
      )
    );
    if (alreadyEnriched) return;

    let cancelled = false;
    (async () => {
      try {
        const token = getToken();
        if (!token) return;
        const res = await axios.get(`${API}/admin/membres/${encodeURIComponent(targetMembreId)}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!cancelled && res.data) {
          // Fusion : on préserve les overrides éventuels présents dans
          // membreData (URL params), et on enrichit avec les données DB.
          setMembreData(prev => ({ ...prev, ...res.data }));

          // V37.43 — Auto-pre-fill du CA HT depuis la derniere proposition
          // BATICO du membre (si on ne l'a pas deja saisi). Cela evite que
          // le COOPER doive ressaisir le CA pour relancer une simulation,
          // et evite surtout l'effet "bouton Calculer muet" quand le CA
          // n'a pas ete renseigne.
          const derniere = res.data?.derniere_proposition_batico;
          if (derniere?.ca_ht && !cancelled) {
            setFormData(prev => {
              if (prev?.ca_ht && String(prev.ca_ht).trim() !== '') {
                // L'utilisateur (ou un autre prefill) a deja saisi : on ne ecrase pas
                return prev;
              }
              return { ...prev, ca_ht: String(derniere.ca_ht) };
            });
            // Si la derniere proposition etait du nominatif et qu'on a un
            // nombre_jours valide, on le pre-remplit aussi (sans toucher au mode
            // pour ne pas surprendre l'utilisateur).
            if (derniere?.nombre_jours) {
              setFormData(prev => {
                if (prev?.nombre_jours && String(prev.nombre_jours).trim() !== '') return prev;
                return { ...prev, nombre_jours: String(derniere.nombre_jours) };
              });
            }
          }

          // V38.00 — Fallback : CA HT pré-rempli depuis le PROFIL du membre
          // (champ chiffre_affaires du tableau des membres) si aucune
          // proposition antérieure et aucune saisie utilisateur.
          const caProfil = parseFloat(
            String(res.data?.chiffre_affaires ?? '').replace(/[^\d.,-]/g, '').replace(',', '.')
          );
          if (!derniere?.ca_ht && Number.isFinite(caProfil) && caProfil > 0 && !cancelled) {
            setFormData(prev => {
              if (prev?.ca_ht && String(prev.ca_ht).trim() !== '') return prev;
              return { ...prev, ca_ht: String(Math.round(caProfil)) };
            });
          }
        }
      } catch (err) {
        // Silencieux : si le membre n'est pas trouvé ou access denied,
        // on retombe sur le comportement par défaut (membre = user).
        if (process.env.NODE_ENV !== 'production') {
          console.debug('Membre cible non chargé:', err?.response?.status);
        }
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line
  }, [user, location.search, membreData?.effectif, membreData?.date_creation_entreprise, membreData?.experience_professionnelle_annees]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const propositionId = params.get('proposition');
    const propositionToken = params.get('token');
    const fromBaticoResume = params.get('from') === 'batico_resume';
    // Deux modes : (a) lien email signé OU (b) reprise authentifiée
    if (!propositionId || (!propositionToken && !fromBaticoResume)) return;

    // Bloque l'auto-calcul : le montant restauré depuis la proposition doit
    // rester affiché tel quel (sinon le recalcul peut donner un montant
    // différent de la proposition d'origine).
    if (autoCalcDoneRef) autoCalcDoneRef.current = true;

    let cancelled = false;
    (async () => {
      try {
        let response;
        if (propositionToken) {
          // Mode (a) : lien email Brevo public avec token signé
          response = await axios.get(
            `${API}/batico/proposition/${encodeURIComponent(propositionId)}`,
            { params: { token: propositionToken } },
          );
        } else {
          // Mode (b) : reprise authentifiée depuis le tableau membres
          const authToken = getToken();
          response = await axios.get(
            `${API}/batico/proposition/by-devis/${encodeURIComponent(propositionId)}/snapshot`,
            { headers: { Authorization: `Bearer ${authToken}` } },
          );
        }
        if (cancelled) return;
        const data = response.data || {};
        const snapshot = data.snapshot || data.snapshot_payload || data;

        // Restaurer le formulaire (CA + mode + jours) pour cohérence visuelle.
        setFormData(prev => ({
          ...prev,
          ca_ht: String(snapshot.ca_ht || prev.ca_ht || ''),
          nombre_jours: String(snapshot.nombre_jours || prev.nombre_jours || ''),
        }));
        if (snapshot.type_souscription === 'nominative') {
          setModeCalcul('nominative');
        }
        if (snapshot.membre_prefill) {
          setMembreData(snapshot.membre_prefill);
        }

        // Reprise intégrale : opération, maître d'ouvrage, personnalisations Super Admin.
        if (snapshot.operation_nominative) {
          setOperationNominative(prev => ({ ...prev, ...Object.fromEntries(
            Object.entries(snapshot.operation_nominative).filter(([, v]) => v !== null && v !== undefined),
          ) }));
        }
        if (snapshot.maitre_ouvrage && Object.keys(snapshot.maitre_ouvrage).length) {
          setFormData(prev => ({ ...prev, maitre_ouvrage: snapshot.maitre_ouvrage }));
        }
        const mp = snapshot.montants_personnalises;
        if (mp && setMontantsPersonnalises) {
          // Montant HT déjà remisé : la remise n'est pas réappliquée.
          setMontantsPersonnalises({
            enabled: true,
            ca_personnalise: mp.ca_personnalise != null ? String(mp.ca_personnalise) : '',
            montant_ht_personnalise: mp.montant_ht_personnalise != null ? String(mp.montant_ht_personnalise) : '',
            montant_ttc_personnalise: mp.montant_ttc_personnalise != null ? String(mp.montant_ttc_personnalise) : '',
            remise_pourcentage: '', remise_euros: '',
          });
        }
        if (Array.isArray(snapshot.taxes_personnalisees) && snapshot.taxes_personnalisees.length && setTaxesPersonnalisees) {
          setTaxesPersonnalisees({
            enabled: true,
            lignes: snapshot.taxes_personnalisees.map((t, i) => ({
              id: `reprise-${i}`, actif: true, nom: t.nom || '',
              taux: t.taux != null ? String(t.taux) : '', montant: t.taux != null ? '' : String(t.montant ?? ''),
            })),
          });
        }
        if (snapshot.periode_personnalisee && setValiditePersonnalisee) {
          setValiditePersonnalisee({ enabled: true, ...snapshot.periode_personnalisee });
        }

        // Restaurer les activités sélectionnées (gros oeuvre, second oeuvre,
        // travaux publics, PIB). Le snapshot peut stocker soit
        // `activites_selectionnees` (objet { gros_oeuvre, second_oeuvre,
        // travaux_publics, pib } d'IDs) — format actuel — soit
        // `activites_details` (objet de listes d'activités complètes) — fallback
        // pour les anciennes propositions.
        const actsSel = snapshot.activites_selectionnees || {};
        const actsDetails = snapshot.activites_details || {};
        const extractIds = (key) => {
          const sel = actsSel[key];
          if (Array.isArray(sel) && sel.length > 0) return sel;
          const det = actsDetails[key];
          if (Array.isArray(det) && det.length > 0) {
            return det.map((a) => a?.id).filter(Boolean);
          }
          return [];
        };
        const goIds = extractIds('gros_oeuvre');
        const secondIds = extractIds('second_oeuvre');
        const tpIds = extractIds('travaux_publics');
        const pibIds = extractIds('pib');
        if (goIds.length > 0) setSelectedActivitesGO(goIds);
        if (secondIds.length > 0) setSelectedActivites2nd(secondIds);
        if (tpIds.length > 0) setSelectedActivitesTP(tpIds);
        if (pibIds.length > 0) setSelectedActivitesPIB(pibIds);

        // Reprise d'une proposition d'avenant : mode avenant + contrat parent + prorata
        if (snapshot.avenant && (snapshot.avenant.parent_id || snapshot.avenant.numero_police)) {
          setModeSimulateur('avenant');
          if (snapshot.avenant.prorata_config) ecrireProrataConfig(snapshot.avenant.prorata_config);
          try {
            const rp = await axios.get(
              `${API}/souscriptions/search-by-reference/${encodeURIComponent(snapshot.avenant.parent_id || snapshot.avenant.numero_police)}`,
              { headers: { Authorization: `Bearer ${getToken()}` } });
            if (!cancelled && rp.data?.souscription) setContratParent(rp.data.souscription);
          } catch {
            if (!cancelled) setContratParent({ id: snapshot.avenant.parent_id, numero_police: snapshot.avenant.numero_police, date_fin: snapshot.avenant.date_fin });
          }
        }

        // Restaurer le RÉSULTAT de la proposition d'origine : le panneau
        // « Résultat de la Simulation » doit afficher le MÊME montant que la
        // proposition (pas un recalcul qui peut différer si les tarifs ou le
        // profil d'éligibilité ont changé depuis l'émission).
        const tarif = data.tarification || snapshot.tarification || {};
        const montantTtcRestaure = data.montant_ttc || tarif.montant_ttc;
        if (montantTtcRestaure) {
          setResultat({
            ...tarif,
            montant_ht: data.montant_ht ?? tarif.montant_ht ?? 0,
            montant_ttc: montantTtcRestaure,
            taxes: tarif.taxes || {},
            detail_calcul: tarif.detail_calcul || {},
          });
        }

        // Resauvegarder localement pour cohérence avec le flux session courant.
        try {
          sessionStorage.setItem(
            `batico_proposition_${propositionId}`,
            JSON.stringify(snapshot)
          );
          sessionStorage.setItem('batico_proposition_latest', propositionId);
        } catch (_) {}

        // Reconstituer la date de validité affichée dans la bannière.
        let dateValiditeStr = '';
        try {
          dateValiditeStr = data.expires_at
            ? new Date(data.expires_at).toLocaleDateString('fr-FR', {
                day: '2-digit', month: 'long', year: 'numeric',
              })
            : '';
        } catch (_) {
          dateValiditeStr = '';
        }

        setPropositionConversion({
          devisId: propositionId,
          validiteJours: data.validite_jours || 365,
          dateValidite: dateValiditeStr,
          dateValiditeISO: data.expires_at || '',
          restoredFromEmail: !!propositionToken,
        });

        // Versioning : la prochaine génération PDF sera enregistrée comme
        // une nouvelle version de la lignée (v_n+1). On utilise l'ID racine
        // de la lignée si présent dans la réponse, sinon le devis_id reçu.
        setResumeParentDevisId(data.parent_devis_id || propositionId);

        toast.info(
          propositionToken ? 'Proposition ESS restaurée' : 'Proposition ESS rechargée dans le simulateur',
          {
            description: propositionToken
              ? 'Reprenez votre souscription en 1 clic.'
              : `Reprise de la proposition ${propositionId.slice(0, 14)}… pour ajustement.`,
            duration: 6000,
          },
        );
      } catch (error) {
        if (cancelled) return;
        const status = error?.response?.status;
        if (status === 404) {
          toast.warning('Lien expiré ou invalide', {
            description: 'Cette proposition n\'est plus disponible. Lancez une nouvelle simulation.',
            duration: 6000,
          });
        }
      }
    })();

    return () => { cancelled = true; };
  }, [location.search]);

  // Reprise d'une souscription RCD/BATICO depuis l'espace membre
  // (« Rouvrir dans le simulateur ») : restaure les activités cochées,
  // le CA et le type de souscription pour modification puis nouvelle version.
  // URL : /calculateur-batico?from=souscription_resume&souscription=<id>
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const souscriptionId = params.get('souscription');
    const fromSouscriptionResume = params.get('from') === 'souscription_resume';
    if (!souscriptionId || !fromSouscriptionResume) return;

    // Bloque l'auto-calcul : le montant restauré depuis la souscription doit
    // rester affiché tel quel jusqu'à un recalcul explicite de l'utilisateur.
    if (autoCalcDoneRef) autoCalcDoneRef.current = true;

    let cancelled = false;
    (async () => {
      try {
        const token = getToken();
        const response = await axios.get(
          `${API}/souscriptions-rcd/${encodeURIComponent(souscriptionId)}`,
          { headers: { Authorization: `Bearer ${token}` } },
        );
        if (cancelled) return;
        const sub = response.data || {};

        setFormData(prev => ({
          ...prev,
          ca_ht: String(sub.chiffre_affaires || sub.montant_ht_contribution || prev.ca_ht || ''),
          nombre_jours: String(sub.nombre_jours || prev.nombre_jours || ''),
        }));
        if (sub.type_souscription === 'nominative') {
          setModeCalcul('nominative');
        }

        setMembreData(prev => ({
          ...prev,
          membre_id: sub.membre_id || sub.member_id || prev?.membre_id || null,
          raison_sociale: sub.raison_sociale || prev?.raison_sociale || '',
          siret: sub.siret || prev?.siret || '',
          territoire: sub.territoire || prev?.territoire || '',
        }));

        // Restaurer les activités cochées : `activites_selectionnees` (IDs,
        // format actuel) avec fallback `activites_details` (objets complets).
        const actsSel = sub.activites_selectionnees || {};
        const actsDetails = sub.activites_details || {};
        const toIds = (list) => (Array.isArray(list) ? list : [])
          .map((a) => (typeof a === 'string' ? a : a?.id))
          .filter(Boolean);
        const extractIds = (key) => {
          const sel = toIds(actsSel[key]);
          return sel.length > 0 ? sel : toIds(actsDetails[key]);
        };
        const goIds = extractIds('gros_oeuvre');
        const secondIds = extractIds('second_oeuvre');
        const tpIds = extractIds('travaux_publics');
        const pibIds = extractIds('pib');
        if (goIds.length > 0) setSelectedActivitesGO(goIds);
        if (secondIds.length > 0) setSelectedActivites2nd(secondIds);
        if (tpIds.length > 0) setSelectedActivitesTP(tpIds);
        if (pibIds.length > 0) setSelectedActivitesPIB(pibIds);

        // Versioning lié : rattacher la prochaine proposition générée à la
        // lignée de la proposition ESS d'origine (v2, v3…) si la souscription
        // référence son devis source. Sinon, nouvelle lignée (v1).
        const lineageDevisId = sub.devis_id || sub.source_devis_id || null;
        if (lineageDevisId) setResumeParentDevisId(lineageDevisId);

        // Marque la session comme « Reprise simulateur » → badge sur la
        // nouvelle proposition dans Mes Documents.
        setRepriseSimulateur(true);

        // Restaurer le MONTANT de la souscription pour que le panneau
        // « Résultat de la Simulation » affiche le même montant qu'à
        // l'origine (et non un recalcul potentiellement différent).
        const subTarif = sub.tarification || {};
        const subTtc = sub.montant_ttc || subTarif.montant_ttc || sub.montant_global;
        if (subTtc) {
          setResultat({
            ...subTarif,
            montant_ht: subTarif.montant_ht || sub.montant_ht_contribution || sub.montant_ht || 0,
            montant_ttc: subTtc,
            taxes: subTarif.taxes || {},
            detail_calcul: subTarif.detail_calcul || {},
          });
        }

        const totalActs = goIds.length + secondIds.length + tpIds.length + pibIds.length;
        toast.info('Souscription rechargée dans le simulateur', {
          description: totalActs > 0
            ? `${totalActs} activité(s) restaurée(s). Ajustez puis régénérez votre proposition.`
            : 'Ajustez les paramètres puis régénérez votre proposition.',
          duration: 6000,
        });
      } catch (error) {
        if (cancelled) return;
        toast.warning('Reprise impossible', {
          description: 'Cette souscription n\'est pas accessible. Lancez une nouvelle simulation.',
          duration: 6000,
        });
      }
    })();

    return () => { cancelled = true; };
    // eslint-disable-next-line
  }, [location.search]);


  useEffect(() => {
    const tCA = eligibilityForm?.territoires_ca || {};
    const total = Object.values(tCA).reduce((s, v) => s + (Number(v) || 0), 0);
    if (total > 0) {
      setFormData(prev => (
        prev.ca_ht === String(total) ? prev : { ...prev, ca_ht: String(total) }
      ));
    }
  }, [eligibilityForm?.territoires_ca]);

  useEffect(() => {
    const checkFtpeStatus = async () => {
      const params = new URLSearchParams(location.search);
      const memberId = membreData?.membre_id || membreData?.id || params.get('membre_id') || params.get('membre')
        || (['member', 'membre'].includes(user?.role) ? user?.id : null);
      if (!memberId) { setIsFtpeMember(false); return; }
      const token = getToken();
      if (!token) return;
      try {
        const res = await axios.get(`${API}/ftpe-adhesion/member/${memberId}/ftpe-status`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setIsFtpeMember(res.data?.has_ftpe === true);
      } catch {
        setIsFtpeMember(false);
      }
    };
    checkFtpeStatus();
  }, [membreData?.membre_id, membreData?.id, user, location.search]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const fetchRemiseSolidaire = async () => {
      const memberId = membreData?.membre_id;
      if (!memberId) return;
      const token = getToken();
      if (!token) return;
      try {
        const res = await axios.get(`${API}/batico/member/${memberId}/remise-solidaire`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (res.data?.enabled) {
          // En mode reprise (proposition/souscription), NE PAS auto-appliquer la
          // remise ni afficher sa bannière : le panneau résultat doit afficher
          // le MÊME montant que la proposition d'origine, sans personnalisation
          // recalculée.
          const resumeParams = new URLSearchParams(location.search);
          const inResumeMode =
            resumeParams.get('from') === 'batico_resume' ||
            resumeParams.get('from') === 'souscription_resume' ||
            !!resumeParams.get('token');
          if (inResumeMode) return;
          setRemiseSolidaireNegociee(res.data);
          // Auto-apply une seule fois par chargement de membre.
          if (!remiseAutoAppliedRef.current) {
            const pct = Number(res.data.remise_solidaire_max_pct || 0);
            const eur = Number(res.data.remise_solidaire_max_eur || 0);
            setMontantsPersonnalises((prev) => ({
              ...prev,
              enabled: true,
              remise_pourcentage: pct > 0 ? String(pct) : prev.remise_pourcentage,
              remise_euros: eur > 0 ? String(eur) : prev.remise_euros,
            }));
            remiseAutoAppliedRef.current = true;
            const parts = [];
            if (pct > 0) parts.push(`-${pct}%`);
            if (eur > 0) parts.push(`-${eur}€`);
            toast.success('Remise solidaire négociée appliquée', {
              description: `Conditions du membre : ${parts.join(' et ')}`,
              duration: 5000,
            });
          }
        } else {
          setRemiseSolidaireNegociee(null);
        }
      } catch {
        setRemiseSolidaireNegociee(null);
      }
    };
    fetchRemiseSolidaire();
    // Reset le flag d'auto-apply quand on change de membre
    return () => {
      remiseAutoAppliedRef.current = false;
    };
  }, [membreData?.membre_id]);

  useEffect(() => {
    const loadTaxesConfig = async () => {
      try {
        const typeRcds = modeCalcul === 'nominative' ? 'nominatif' : 'annuel';
        const response = await axios.get(`${API}/batico/config/taxes?type_rcds=${typeRcds}`);
        if (response.data.taxes?.length > 0) {
          setTaxesPersonnalisees(prev => ({
            ...prev,
            lignes: response.data.taxes.filter(t => t.actif !== false).map((t, idx) => ({
              id: idx + 1,
              nom: t.nom,
              taux: t.taux,
              montant: '',
              actif: true
            }))
          }));
        }
      } catch (error) {
      }
    };
    loadTaxesConfig();
  }, [modeCalcul]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get('mode') === 'avenant') {
      setModeSimulateur('avenant');
    }
  }, [location.search]);

  useEffect(() => {
    const fetchParametresBatico = async () => {
      try {
        const response = await axios.get(`${API}/parametres-batico`);
        if (response.data) {
          setParametresBatico({
            seuil_ca: response.data.seuil_ca || 40000,
            seuil_ca_actif: response.data.seuil_ca_actif || false,
            coef_gros_oeuvre: response.data.coef_gros_oeuvre || 0.002945,
            coef_second_oeuvre: response.data.coef_second_oeuvre || 0.002695,
            coef_intellectuels: response.data.coef_intellectuels || 0.0045,
            coef_travaux_publics: response.data.coef_travaux_publics || 0.00425,
            // Charger les seuils par type depuis la nouvelle structure
            seuils_ca_par_type: response.data.seuils_ca_par_type || {
              annuel: { 
                seuil_ca: response.data.seuil_ca || 40000, 
                seuil_ca_actif: response.data.seuil_ca_actif || false,
                message: ''
              },
              nominatif: { 
                seuil_ca: 5000, 
                seuil_ca_actif: true,
                message: 'CA minimum 5000€ pour RCDS Nominatif'
              }
            },
            // Coefficients, remises et taxes par type depuis V3
            coefficients_par_type: response.data.coefficients_par_type || {},
            remises_par_type: response.data.remises_par_type || {},
            taxes_par_type: response.data.taxes_par_type || {}
          });
          if (response.data.seuils_ca_par_type) {
          }
        }
      } catch (error) {
      }
    };
    fetchParametresBatico();
  }, []);

  useEffect(() => {
    const fetchRestrictions = async () => {
      try {
        const token = getToken();
        const headers = token ? { Authorization: `Bearer ${token}` } : {};
        const response = await axios.get(`${API}/parametres-batico/restrictions`, { headers });
        // Filtrer seulement les restrictions actives
        const activeRestrictions = (response.data || []).filter(r => r.is_active);
        setRestrictions(activeRestrictions);
      } catch (error) {
        // Silently fail - restrictions are optional
      } finally {
        setLoadingRestrictions(false);
      }
    };
    
    fetchRestrictions();
  }, []);

  useEffect(() => {
    if (contratParent && contratParent.chiffre_affaires) {
      setFormData(prev => ({ ...prev, ca_ht: contratParent.chiffre_affaires.toString() }));
    }
  }, [contratParent]);
}

export default useBaticoEffects;
